import { isEncoded, encodeWithWhitelist, ENCODING_WHITELIST_CHARS } from 'utils';

/**
 * Creates or updates an Adjust tracking link by adding/modifying query parameters.
 *
 * This function takes an existing Adjust link and merges it with new parameters,
 * handling encoding properly to preserve already-encoded URLs from Contentful
 * and special characters defined in the whitelist.
 *
 * Process:
 * 1. Splits the URL into base URL and query string
 * 2. Parses existing query parameters using URLSearchParams (which decodes them)
 * 3. Adds/overwrites parameters from the params object
 * 4. Filters out duplicate and empty parameters
 * 5. Re-encodes parameter values, preserving:
 *    - Already encoded values (prevents double-encoding)
 *    - Whitelisted characters like curly braces used in placeholders
 *
 * @param adjustLink - The base Adjust URL with optional existing query parameters
 * @param params - Object containing key-value pairs to add/update in the URL
 * @returns The complete Adjust URL with merged and properly encoded query parameters
 *
 * @example
 * // Adding new parameters to a base URL
 * createAppAdjustLink('https://app.adjust.com/abc', { campaign: 'summer', creative: 'banner' })
 * // Returns: 'https://app.adjust.com/abc?campaign=summer&creative=banner'
 *
 * @example
 * // Updating existing parameters
 * createAppAdjustLink('https://app.adjust.com/abc?campaign=old', { campaign: 'new' })
 * // Returns: 'https://app.adjust.com/abc?campaign=new'
 *
 * @example
 * // Preserving already-encoded URLs from Contentful
 * createAppAdjustLink(
 *   'https://pixum.go.link?deep_link=com.pixum%3A%2F%2Fopen&redirect=https%3A%2F%2Fapps.apple.com',
 *   { campaign: 'test' }
 * )
 * // Returns: 'https://pixum.go.link?deep_link=com.pixum%3A%2F%2Fopen&redirect=https%3A%2F%2Fapps.apple.com&campaign=test'
 *
 * @example
 * // Preserving curly braces in label parameter
 * createAppAdjustLink('https://app.adjust.com/abc', { label: '{clickid_gclid_123~}' })
 * // Returns: 'https://app.adjust.com/abc?label={clickid_gclid_123~}'
 *
 * @example
 * // Filtering out undefined and null values (empty string params from the original URL are preserved)
 * createAppAdjustLink('https://app.adjust.com/abc', {
 *   campaign: 'test',
 *   adgroup: null,
 *   label: undefined
 * })
 * // Returns: 'https://app.adjust.com/abc?campaign=test'
 *
 * @example
 * // Preserving valueless params from the original URL (e.g. ?ShowProductPB)
 * createAppAdjustLink('https://app.adjust.com/abc?ShowProductPB', { campaign: 'test' })
 * // Returns: 'https://app.adjust.com/abc?ShowProductPB&campaign=test'
 */
export function createAppAdjustLink(adjustLink: string, params: Record<string, unknown>) {
  if (!adjustLink) return '';

  const [baseUrl, queryString = ''] = adjustLink.split('?');
  const searchParams = new URLSearchParams(queryString);

  // Add/update parameters from params object
  for (const [key, value] of Object.entries(params)) {
    if (key && value !== undefined && value !== null && value !== '') {
      searchParams.set(key, value as string);
    }
  }

  // Filter out duplicate keys (preserve valueless params like ?ShowProductPB from original URL)
  const filteredSearchParams = new Map<string, string>();

  for (const [key, value] of searchParams.entries()) {
    if (!filteredSearchParams.has(key)) {
      filteredSearchParams.set(key, value);
    }
  }

  // Reconstruct query string with proper encoding
  const finalQuery = Array.from(filteredSearchParams.entries())
    .map(([key, value]) => {
      // Preserve valueless params (e.g. ?ShowProductPB) without appending "="
      if (value === '') return key;

      // Only encode if the value is not already encoded, preserving whitelisted characters
      const encodedValue = isEncoded(value) ? value : encodeWithWhitelist(value, ENCODING_WHITELIST_CHARS);

      return `${key}=${encodedValue}`;
    })
    .join('&');

  return `${baseUrl}?${finalQuery}`;
}
